import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { consultationsSlice, guidedDoctorConsultsSlice, patientSlice, prescriptionAssistantSlice } from './slices';
import type { ConsultationsSliceType } from './slices/consultations/types';
import type { GuidedDoctorConsultsSliceType } from './slices/guidedDoctorConsults/types';
import type { PatientSliceType } from './slices/patient/types';
import type { PrescriptionAssistantSliceType } from './slices/prescriptionAssistant/types';
import { createSelectors } from './utils/createSelectors';

type AppState = PrescriptionAssistantSliceType &
  GuidedDoctorConsultsSliceType &
  PatientSliceType &
  ConsultationsSliceType;

// https://github.com/pmndrs/zustand/blob/main/docs/guides/typescript.md#slices-pattern
const useAppStoreBase = create<AppState>()(
  /**
   * Recommend using devtools middleware as last as possible.
   * For example, when you use it with immer as a middleware, it should be immer(devtools(...)) and not devtools(immer(...)).
   */

  immer(
    subscribeWithSelector(
      devtools(
        (...items) => ({
          ...prescriptionAssistantSlice(...items),
          ...guidedDoctorConsultsSlice(...items),
          ...patientSlice(...items),
          ...consultationsSlice(...items),
        }),
        // need to serialise Map or Set to display in devtools
        {
          serialize: {
            options: {
              map: true,
              set: true,
            },
          },
        },
      ),
    ),
  ),
);

export const useAppStore = createSelectors(useAppStoreBase);
